import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../../api';
import { useSeason } from '../../context/SeasonContext';
import styles from './MobileLeagueStats.module.css';

const MobileLeagueStats = ({ season, embedded = false }) => {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMetric, setSelectedMetric] = useState(null);
    const { effectiveSeason } = useSeason();
    const selectedSeason = season || effectiveSeason;

    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                const response = await api.getLeagueMetrics(selectedSeason);
                setMetrics(response.data);
            } catch (err) {
                setError('Failed to load metrics');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchMetrics();
    }, [selectedSeason]);

    const containerClass = embedded
        ? `${styles.mobileStats} ${styles.embedded}`
        : styles.mobileStats;

    if (loading) {
        return (
            <div className={containerClass}>
                <div className={styles.loadingState}>
                    <div className={styles.loadingSpinner}></div>
                    <p>{embedded ? 'Loading metrics...' : 'Loading league stats...'}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={containerClass}>
                <div className={styles.errorState}>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!metrics || !metrics.seasonStarted) {
        return (
            <div className={containerClass}>
                <div className={styles.noDataState}>
                    <h3>{metrics?.message || 'No data available'}</h3>
                </div>
            </div>
        );
    }

    const statsCards = [
        {
            id: 'summary',
            title: 'League Summary',
            icon: '📊',
            data: [
                { label: 'Total Picks', value: metrics.summary?.totalPicks || 0 },
                { label: 'Completed', value: metrics.summary?.completedPicks || 0 },
                { label: 'Pending', value: metrics.summary?.pendingPicks || 0 },
                { label: 'Bye Picks', value: metrics.summary?.bye_picks || 0 },
                { label: 'Win Rate', value: `${metrics.summary?.winRate || 0}%` },
            ]
        },
        {
            id: 'losses',
            title: 'Loss Tracking',
            icon: '💰',
            data: metrics.lossTracking ? [
                {
                    label: 'Current Pot',
                    value: `$${metrics.lossTracking?.[metrics.lossTracking.length - 1]?.actual_losses || 0}`
                },
                {
                    label: 'Projected Final',
                    value: `$${metrics.lossTracking?.[metrics.lossTracking.length - 1]?.projected_losses || 0}`
                },
                {
                    label: 'Maximum Possible',
                    value: `$${metrics.lossTracking?.[metrics.lossTracking.length - 1]?.max_possible_losses || 0}`
                },
            ] : []
        },
        {
            id: 'prizes',
            title: 'Prize Distribution',
            icon: '🏆',
            data: metrics.distributionSplits?.current ? [
                {
                    label: '1st Place (80%)',
                    value: `$${metrics.distributionSplits.current.first || 0}`
                },
                {
                    label: '2nd Place (15%)',
                    value: `$${metrics.distributionSplits.current.second || 0}`
                },
                {
                    label: '3rd Place (5%)',
                    value: `$${metrics.distributionSplits.current.third || 0}`
                },
            ] : [
                { label: '1st Place (80%)', value: '$0' },
                { label: '2nd Place (15%)', value: '$0' },
                { label: '3rd Place (5%)', value: '$0' },
            ]
        },
        {
            id: 'performance',
            title: 'Weekly Performance',
            icon: '📈',
            data: metrics.weeklyTrends ? (() => {
                const totalWeeks = metrics.weeklyTrends.length;
                const avgWinRate = (metrics.weeklyTrends.reduce((sum, week) => sum + (week.win_rate || 0), 0) / totalWeeks || 0).toFixed(1);

                // Calculate seasonal performance phases
                const earlyWeeks = metrics.weeklyTrends.filter(w => w.week_number <= 4);
                const midWeeks = metrics.weeklyTrends.filter(w => w.week_number >= 5 && w.week_number <= 12);
                const lateWeeks = metrics.weeklyTrends.filter(w => w.week_number >= 13);

                const earlyWinRate = earlyWeeks.length > 0 ?
                    (earlyWeeks.reduce((sum, week) => sum + (week.win_rate || 0), 0) / earlyWeeks.length).toFixed(1) : 'N/A';
                const midWinRate = midWeeks.length > 0 ?
                    (midWeeks.reduce((sum, week) => sum + (week.win_rate || 0), 0) / midWeeks.length).toFixed(1) : 'N/A';
                const lateWinRate = lateWeeks.length > 0 ?
                    (lateWeeks.reduce((sum, week) => sum + (week.win_rate || 0), 0) / lateWeeks.length).toFixed(1) : 'N/A';

                // Calculate efficiency ratio (avg win margin / avg loss margin)
                const allWinMargins = metrics.weeklyTrends.filter(w => w.best_margin > 0).map(w => w.best_margin);
                const allLossMargins = metrics.weeklyTrends.filter(w => w.worst_margin < 0).map(w => Math.abs(w.worst_margin));
                const avgWinMargin = allWinMargins.length > 0 ? (allWinMargins.reduce((sum, m) => sum + m, 0) / allWinMargins.length) : 0;
                const avgLossMargin = allLossMargins.length > 0 ? (allLossMargins.reduce((sum, m) => sum + m, 0) / allLossMargins.length) : 1;
                const efficiencyRatio = avgLossMargin > 0 ? (avgWinMargin / avgLossMargin).toFixed(2) : 'N/A';

                return [
                    { label: 'Total Weeks', value: totalWeeks },
                    { label: 'Overall Win Rate', value: `${avgWinRate}%` },
                    { label: 'Early Season (W1-4)', value: earlyWinRate !== 'N/A' ? `${earlyWinRate}%` : 'N/A' },
                    { label: 'Mid Season (W5-12)', value: midWinRate !== 'N/A' ? `${midWinRate}%` : 'N/A' },
                    { label: 'Late Season (W13+)', value: lateWinRate !== 'N/A' ? `${lateWinRate}%` : 'N/A' },
                    { label: 'Win Big/Lose Small', value: `${efficiencyRatio}x` },
                    {
                        label: 'Season Trend',
                        value: (() => {
                            if (earlyWinRate === 'N/A' || lateWinRate === 'N/A') return 'Insufficient data';
                            const early = parseFloat(earlyWinRate);
                            const late = parseFloat(lateWinRate);
                            const diff = late - early;
                            if (Math.abs(diff) < 2) return 'Stable';
                            return diff > 0 ? `Improving (+${diff.toFixed(1)}%)` : `Declining (${diff.toFixed(1)}%)`;
                        })()
                    }
                ];
            })() : []
        },
        {
            id: 'users',
            title: 'User Performance',
            icon: '👥',
            data: metrics.userPerformance ? [
                { label: 'Active Users', value: metrics.userPerformance.length },
                {
                    label: 'Top Performer',
                    value: metrics.userPerformance[0]?.username || 'N/A'
                },
                {
                    label: 'Avg Margin',
                    value: `${(metrics.userPerformance.reduce((sum, user) => sum + (Number(user.avg_margin) || 0), 0) / metrics.userPerformance.length || 0).toFixed(1)}`
                },
            ] : []
        },
        {
            id: 'leadtime',
            title: 'Lead Time Analysis',
            icon: '⏱️',
            data: metrics.leadTimeStats && Array.isArray(metrics.leadTimeStats) && metrics.leadTimeStats.length > 0 ? [
                {
                    label: 'Avg Lead Time',
                    value: `${(metrics.leadTimeStats.reduce((sum, stat) => sum + (Number(stat.avg_lead_time) || 0), 0) / metrics.leadTimeStats.length).toFixed(1)} hrs`
                },
                {
                    label: 'Min Lead Time',
                    value: `${Math.min(...metrics.leadTimeStats.map(stat => Number(stat.avg_lead_time) || Infinity)).toFixed(1)} hrs`
                },
                {
                    label: 'Max Lead Time',
                    value: `${Math.max(...metrics.leadTimeStats.map(stat => Number(stat.avg_lead_time) || 0)).toFixed(1)} hrs`
                },
            ] : [
                { label: 'Avg Lead Time', value: 'No data' },
                { label: 'Min Lead Time', value: 'No data' },
                { label: 'Max Lead Time', value: 'No data' },
            ]
        },
        {
            id: 'picktype',
            title: 'Pick Types',
            icon: '🏈',
            data: metrics.pickTypePerformance?.length > 0 ?
                // Remove duplicates by creating a Map keyed by pick_category
                Array.from(
                    new Map(
                        metrics.pickTypePerformance.map(type => [type.pick_category, type])
                    ).values()
                )
                .map(type => {
                    const wins = Number(type.wins) || 0;
                    const losses = Number(type.losses) || 0;
                    const winRate = Number(type.win_percentage) || 0;
                    const typeName = type.pick_category || 'Unknown';

                    return {
                        label: typeName,
                        value: `${wins}-${losses} (${winRate.toFixed(1)}%)`,
                        sortValue: winRate
                    };
                })
                .sort((a, b) => b.sortValue - a.sortValue)
                : [{ label: 'No data', value: 'available' }]
        },
        {
            id: 'location',
            title: 'Pick Locations',
            icon: '🏠',
            data: (() => {
                if (!metrics.pickLocationAnalysis || typeof metrics.pickLocationAnalysis !== 'object') {
                    return [{ label: 'No data', value: 'available' }];
                }

                // Handle if it's an array (like pick types)
                if (Array.isArray(metrics.pickLocationAnalysis)) {
                    return metrics.pickLocationAnalysis.map(location => ({
                        label: location.location || location.pick_location || 'Unknown',
                        value: `${location.wins || 0}-${location.losses || 0} (${(location.win_percentage || 0).toFixed(1)}%)`
                    })).sort((a, b) => {
                        const aRate = parseFloat(a.value.match(/\(([^)]+)%\)/)?.[1] || '0');
                        const bRate = parseFloat(b.value.match(/\(([^)]+)%\)/)?.[1] || '0');
                        return bRate - aRate;
                    });
                }

                // Handle if it's the sunburst object structure
                if (metrics.pickLocationAnalysis.children) {
                    const locations = [];

                    // Navigate the sunburst hierarchy: root -> bet types -> locations -> wins/losses
                    metrics.pickLocationAnalysis.children.forEach((betType, btIndex) => {
                        console.log(`Bet Type ${btIndex} (${betType.id}):`, betType);
                        if (betType.children) {
                            betType.children.forEach((location, locIndex) => {
                                console.log(`  Location ${locIndex} (${location.id}):`, location);
                                if (location.id && location.id !== 'Wins' && location.id !== 'Losses' && location.children) {
                                    console.log(`    Location children:`, location.children);
                                    const winsChild = location.children.find(child => child.name === 'Wins');
                                    const lossesChild = location.children.find(child => child.name === 'Losses');
                                    console.log(`    Wins child:`, winsChild);
                                    console.log(`    Losses child:`, lossesChild);
                                    const wins = winsChild?.value || 0;
                                    const losses = lossesChild?.value || 0;
                                    const totalPicks = wins + losses;
                                    const winRate = totalPicks > 0 ? ((wins / totalPicks) * 100) : 0;
                                    console.log(`    Calculated: ${wins} wins, ${losses} losses, ${winRate.toFixed(1)}% rate`);

                                    // Check if this location already exists (combine across bet types)
                                    const existingLocation = locations.find(loc => loc.label === location.id);
                                    if (existingLocation) {
                                        existingLocation.wins += wins;
                                        existingLocation.losses += losses;
                                        existingLocation.total += totalPicks;
                                        existingLocation.winRate = existingLocation.total > 0 ?
                                            ((existingLocation.wins / existingLocation.total) * 100) : 0;
                                        existingLocation.value = `${existingLocation.wins}-${existingLocation.losses} (${existingLocation.winRate.toFixed(1)}%)`;
                                    } else {
                                        locations.push({
                                            label: location.id,
                                            value: `${wins}-${losses} (${winRate.toFixed(1)}%)`,
                                            wins: wins,
                                            losses: losses,
                                            total: totalPicks,
                                            winRate: winRate
                                        });
                                    }
                                }
                            });
                        }
                    });

                    return locations.sort((a, b) => b.winRate - a.winRate);
                }

                return [{ label: 'No data', value: 'available' }];
            })()
        }
    ];

    const handleCardTap = (card) => {
        setSelectedMetric(card);
    };

    const handleBackTap = () => {
        setSelectedMetric(null);
    };

    if (selectedMetric) {
        return (
            <div className={containerClass}>
                <div className={styles.detailView}>
                    <div className={styles.detailHeader}>
                        <button
                            type="button"
                            className={styles.backButton}
                            onClick={handleBackTap}
                            aria-label="Back to metrics list"
                        >
                            ← Back
                        </button>
                        <h2 className={styles.detailTitle}>
                            <span className={styles.detailIcon}>{selectedMetric.icon}</span>
                            {selectedMetric.title}
                        </h2>
                    </div>
                    <div className={styles.detailContent}>
                        {selectedMetric.data.map((item) => (
                            <div key={`${selectedMetric.id}-${item.label}`} className={styles.detailItem}>
                                <span className={styles.detailLabel}>{item.label}</span>
                                <span className={styles.detailValue}>{item.value}</span>
                            </div>
                        ))}
                    </div>

                    {/* Add explanations for Weekly Performance */}
                    {selectedMetric.id === 'performance' && (
                        <div className={styles.detailExplanation}>
                            <h4>📊 What This Means:</h4>
                            <div className={styles.explanationItem}>
                                <strong>Early vs Mid vs Late:</strong> Shows if the league performs better when lines are "softer" (early season) or "tighter" (late season)
                            </div>
                            <div className={styles.explanationItem}>
                                <strong>Win Big/Lose Small:</strong> Ratio above 1.0x means winning by more points than losing by (good risk management). Below 1.0x suggests taking risky picks.
                            </div>
                            <div className={styles.explanationItem}>
                                <strong>Season Trend:</strong> Improving trend suggests the league learns over time. Declining may indicate lines get tighter or early season had more obvious picks.
                            </div>
                        </div>
                    )}

                    <div className={styles.detailNote}>
                        <p>📱 Tap "Back" to return to the main stats view</p>
                        <p>🖥️ For detailed charts and analysis, use the desktop version</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={containerClass}>
            {!embedded && (
                <div className={styles.headerSection}>
                    <h1 className={styles.pageTitle}>League Stats</h1>
                    <p className={styles.seasonInfo}>Season {selectedSeason}</p>
                </div>
            )}

            <div className={styles.statsGrid}>
                {statsCards.map((card) => (
                    <div
                        key={card.id}
                        className={styles.statsCard}
                        onClick={() => handleCardTap(card)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleCardTap(card);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`View ${card.title} details`}
                    >
                        <div className={styles.cardHeader}>
                            <span className={styles.cardIcon}>{card.icon}</span>
                            <h3 className={styles.cardTitle}>{card.title}</h3>
                        </div>
                        <div className={styles.cardPreview}>
                            {card.data.slice(0, 2).map((item) => (
                                <div key={`${card.id}-preview-${item.label}`} className={styles.previewItem}>
                                    <span className={styles.previewLabel}>{item.label}:</span>
                                    <span className={styles.previewValue}>{item.value}</span>
                                </div>
                            ))}
                            {card.data.length > 2 && (
                                <div className={styles.moreIndicator}>
                                    +{card.data.length - 2} more →
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.footerNote}>
                <p>📊 Tap any card for detailed information</p>
                <p>🖥️ For interactive charts and advanced analysis, use the desktop version</p>
            </div>
        </div>
    );
};

MobileLeagueStats.propTypes = {
    season: PropTypes.number,
    embedded: PropTypes.bool,
};

export default MobileLeagueStats;
